/**
 * FeedbackForm
 * Reusable feedback form component that can be rendered inline
 */

import React, { useState } from 'react';
import { Button, Form, Icon, TextArea } from 'ui-component-library/base';
import { TextHtmlModel } from 'types';
import Link from 'components/Link';

interface Props {
	commentHeading?: string;
	commentNoticeText?: TextHtmlModel;
	apiUrl?: string;
	initialSelection?: 'Ja' | 'Nej';
	onClose: () => void;
}

const FeedbackForm: React.FC<Props> = ({
	commentHeading = 'Vill du lämna en kommentar?',
	commentNoticeText,
	apiUrl = '/api/sv/feedback/send',
	initialSelection = 'Nej',
	onClose,
}) => {
	const [helpful, setHelpful] = useState<'Ja' | 'Nej'>(initialSelection);
	const [comment, setComment] = useState('');
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const payload = {
				url: window.location.href,
				helpful: helpful,
				comment: comment,
			};

			const response = await fetch(apiUrl || '/api/sv/feedback/send', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				throw new Error('Failed to submit feedback');
			}

			setIsSubmitted(true);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Ett fel uppstod vid överföring'
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleReset = () => {
		setHelpful(initialSelection);
		setComment('');
		setIsSubmitted(false);
		setError(null);
		onClose();
	};

	if (isLoading) {
		return (
			<div className="flex justify-center p-4 w-full">
				<Icon
					icon="loader"
					size={3}
					animate="spin"
					color="#1B7765"
					aria-label="Laddar"
				/>
			</div>
		);
	}

	if (isSubmitted) {
		return (
			<div>
				<p className="font-standard text-p m-0 mb-6 text-black">
					Tack för din åsikt! Din kommentar har skickats.
				</p>
			</div>
		);
	}

	return (
		<>
			{/* Comment Section Title */}
			<h3 className="font-alt font-semibold text-h5 m-0 mb-4 text-black">
				{commentHeading}
			</h3>

			<Form
				className="w-full"
				onSubmit={handleSubmit}
				onChange={(e: { name: string; value: string }) => {
					if (e.name === 'comment') {
						setComment(e.value);
					}
				}}
			>
				<div className="mb-6">
					<TextArea
						name="comment"
						id="comment"
						label="Comment"
						placeholder="Lämna gärna en kommentar. Det hjälper oss att förbättra vår webbplats."
						defaultValue={comment}
					/>
					<p className="font-standard text-sm mt-2 text-greyDarker">
						Notera att detta enbart är ett kommentarsfält, för frågor vänligen
						använd{' '}
						<Link href="/frageportalen/">
							<span className="text-blue underline">Frågportalen</span>
						</Link>
						.
					</p>
				</div>

				{/* Error Message */}
				{error && <p className="text-red text-p mt-4 mb-4">{error}</p>}

				{/* Action Buttons */}
				<div className="mb-8 flex gap-3">
					<Button type="submit" color="teal" disabled={!comment.trim()}>
						Skicka
					</Button>
					<Button variant="secondary" onClick={handleReset}>
						Avbryt
					</Button>
				</div>
			</Form>
		</>
	);
};

export default FeedbackForm;
